import { EmptyState } from '@/components/empty-state';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { MAX_ASSET_BYTES, useAssets, useDeleteAsset, useUploadAsset, type Asset } from '@/lib/queries';
import { useContextStore } from '@/stores/context';
import { createFileRoute } from '@tanstack/react-router';
import { FileType, Trash2, Upload } from 'lucide-react';
import { useRef, useState } from 'react';
import { toast } from 'sonner';

export const Route = createFileRoute('/_authed/assets')({ component: AssetsPage });

const ACCEPT = 'image/*,font/woff2,font/ttf,font/otf';

function AssetsPage(): React.JSX.Element {
  const appId = useContextStore((s) => s.appId);
  const { data: assets, isLoading } = useAssets(appId);
  const upload = useUploadAsset(appId);
  const remove = useDeleteAsset(appId);

  const input = useRef<HTMLInputElement>(null);
  const [pendingDelete, setPendingDelete] = useState<Asset | null>(null);

  function pick(file: File | undefined) {
    if (!file) return;
    // Third and last place the 500 KB law is enforced (hook throws, server 413s). It is here so the
    // author learns before a 500 KB upload crosses the wire, not after.
    if (file.size > MAX_ASSET_BYTES) {
      toast.error(`${file.name} is ${Math.round(file.size / 1024)} KB — the limit is 500 KB.`);
      return;
    }
    upload.mutate(file, {
      onSuccess: () => toast.success(`Uploaded ${file.name}`),
      onError: (error: Error) => toast.error(error.message),
    });
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Assets</h1>
          <p className="text-sm text-muted-foreground">Images and fonts, up to 500 KB each.</p>
        </div>
        <Button onClick={() => input.current?.click()} disabled={upload.isPending || !appId}>
          <Upload className="size-4" />
          {upload.isPending ? 'Uploading…' : 'Upload'}
        </Button>
      </div>

      <input
        ref={input}
        type="file"
        accept={ACCEPT}
        className="hidden"
        onChange={(event) => {
          pick(event.target.files?.[0]);
          event.target.value = '';
        }}
      />

      {isLoading ? (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-40 rounded-xl" />
          ))}
        </div>
      ) : !assets?.length ? (
        <EmptyState
          title="No assets yet"
          description="Upload the images and fonts your experiences reference. Anything larger than 500 KB is rejected — it has to travel to a phone."
          action={
            <Button onClick={() => input.current?.click()} disabled={!appId}>
              <Upload className="size-4" />
              Upload an asset
            </Button>
          }
        />
      ) : (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {assets.map((asset) => (
            <div key={asset.id} className="group relative overflow-hidden rounded-xl border">
              <div className="flex h-32 items-center justify-center bg-secondary/40">
                {asset.content_type.startsWith('image/') ? (
                  <img src={asset.url} alt="" className="size-full object-contain" />
                ) : (
                  <span className="flex items-center gap-2 rounded-lg border bg-background px-2.5 py-1.5 text-xs">
                    <FileType className="size-4 text-muted-foreground" />
                    {asset.content_type.replace('font/', '').toUpperCase()}
                  </span>
                )}
              </div>

              <div className="flex items-center justify-between gap-2 border-t px-3 py-2">
                <div className="min-w-0">
                  <p className="truncate text-xs font-medium">{asset.r2_key.split('/').pop()}</p>
                  <p className="text-xs text-muted-foreground">
                    {asset.width && asset.height ? `${asset.width}×${asset.height}` : asset.content_type}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label="Delete asset"
                  className="shrink-0 text-muted-foreground hover:text-destructive"
                  onClick={() => setPendingDelete(asset)}
                >
                  <Trash2 className="size-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <AlertDialog open={pendingDelete !== null} onOpenChange={(open) => !open && setPendingDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this asset?</AlertDialogTitle>
            <AlertDialogDescription>
              Published bundles that already reference it keep working; drafts pointing at it will break.
              This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-white hover:bg-destructive/90"
              onClick={() => {
                const asset = pendingDelete;
                if (!asset) return;
                remove.mutate(asset.id, {
                  onSuccess: () => toast.success('Asset deleted'),
                  onError: (error: Error) => toast.error(error.message),
                });
                setPendingDelete(null);
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
